import { View } from '@/components/ui/view';
import { Text } from '@/components/ui/text';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { GlobalState } from '@/containers/GlobalState';
import i18n from 'i18n-js';
import Moment from 'moment';
import React, { useEffect, useState, useContext } from 'react';
import { useSelector } from 'react-redux';
import { rrulestr } from 'rrule';
import Footer from '@/components/basic/Footer';
import WorkflowListElement from '@/components/workflow/WorkflowListElement';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FlatList } from 'react-native-gesture-handler';
import InAInfo from '@/components/basic/InAInfo';

const repeat = ['yearly', 'monthly', 'weekly', 'daily', 'once'];

const Tab = createMaterialTopTabNavigator();
const greet = ['homescreen.greetNight', 'homescreen.greetMorning', 'homescreen.greetDay', 'homescreen.greetEvening'];

const ITEM_HEIGHT = 72;
const getItemLayout = (item, index) => ({ length: ITEM_HEIGHT, offset: ITEM_HEIGHT * index, index: index });

function ActiveScreen({ navigation }) {
    const insets = useSafeAreaInsets();
    const server = useSelector((state) => state.server);
    const tasks = useSelector((state) => state.tasks.active);
    const changed = useSelector((state) => state.changed);
    const settings = useSelector((state) => state.settings);

    const renderItem = ({ item, index }) => {
        return <WorkflowListElement navigation={navigation} taskid={item} showProgress={true} showTimer={true} />;
    };

    return <View className="flex-1 bg-secondary-50">{tasks.length == 0 ? <InAInfo info={(!changed && (settings.showInA == undefined || settings.showInA) ? i18n.t('homescreen.infoActiveInA') : '') + i18n.t('homescreen.infoActive')} pose={0} /> : <FlatList bg="$secondary50" contentContainerStyle={{ paddingBottom: server.online ? 140 + insets.bottom : 90 + insets.bottom }} data={tasks} renderItem={renderItem} keyExtractor={(item, index) => item || 'H' + index} initialNumToRender={8} maxToRenderPerBatch={10} windowSize={10} getItemLayout={getItemLayout} decelerationRate={0} snapToInterval={ITEM_HEIGHT} snapToAlignment="start" paddingTop={7} />}</View>;
}

function PlannedScreen({ navigation }) {
    const insets = useSafeAreaInsets();
    const user = useSelector((state) => state.user);
    const server = useSelector((state) => state.server);
    const workflows = useSelector((state) => state.workflows);
    const wfMap = useSelector((state) => state.wfMap || []);
    const planned = useSelector((state) => state.planned);
    const [plans, setPlans] = useState([]);

    useEffect(() => {
        if (planned.plans) {
            const date = Moment().startOf('day').toISOString();
            let plans = planned.plans
                .filter((plan) => undefined != wfMap[plan.workflowId] && undefined != workflows[wfMap[plan.workflowId]] && !workflows[wfMap[plan.workflowId]].deleted)
                .filter((plan) => rrulestr(plan.schedule).between(new Date(Moment(date).subtract(1, 'ms')), new Date(Moment(date).endOf('day'))).length > 0)
                .map((plan) => ({ ...plan, taskid: wfMap[plan.workflowId] }))
                .sort((a, b) => (a.startAt > b.startAt ? 1 : a.startAt < b.startAt ? -1 : 0));
            setPlans(plans.sort((a, b) => a.startAt > b.startAt));
        }
    }, [planned, workflows]);

    const renderItem = ({ item, index }) => {
        let freq = i18n.t('scheduleWorkflow.' + repeat[4]).toLowerCase();
        let info;
        if (item.schedule) {
            const rule = rrulestr(item.schedule);
            if (rule.options.freq != 3 || rule.options.count != 1) {
                freq = i18n.t('scheduleWorkflow.' + repeat[rule.options.freq]).toLowerCase();
            }
            const time = Moment(rule.options.dtstart).format('HH:mm');
            info = i18n.t(time == '00:00' ? 'scheduleWorkflow.allDaySchedule' : 'scheduleWorkflow.schedule', { time: time, freq: freq });
        }
        return item.info ? (
            <Text key={'T' + index} className="px-5 text-xl text-secondary-900">
                {item.info}
            </Text>
        ) : (
            <WorkflowListElement navigation={navigation} taskid={item.taskid} showProgress={true} isDone={item?.doneAt ? Moment().isSame(Moment(item?.doneAt + 'Z'), 'date') : false} info={info} />
        );
    };

    return <View className="flex-1 bg-secondary-50">{plans.length == 0 ? <InAInfo info={i18n.t('homescreen.infoPlanned')} pose={1} /> : <FlatList bg="$secondary50" contentContainerStyle={{ paddingBottom: server.online ? 140 + insets.bottom : 90 + insets.bottom }} data={plans} renderItem={renderItem} keyExtractor={(item, index) => item.id || 'T' + index} initialNumToRender={8} maxToRenderPerBatch={10} windowSize={10} getItemLayout={getItemLayout} decelerationRate={0} snapToInterval={ITEM_HEIGHT} snapToAlignment="start" paddingTop={7} />}</View>;
}

export default function HomeScreen({ navigation }) {
    const server = useSelector((state) => state.server);
    const user = useSelector((state) => state.user);
    const notify = useSelector((state) => state.notify || {});

    const { inAColors } = useContext(GlobalState);

    const actions = [
        { icon: server.online ? 'qr-code-scanner' : '', text: 'footer.qrcode', component: 'Alternatives', params: { title: 'shareQRCode.scanGo', screenA: 'ScanQRCode', textA: 'shareQRCode.scanQR', screenB: 'PublicWorkflows', paramsB: { isEdit: false }, textB: 'shareQRCode.taskQR', hint: 'shareQRCode.hint' } },
        { icon: 'format-list-bulleted', text: 'footer.list', component: 'Workflows', marker: notify.tasklist },
        { icon: 'event-available', text: 'footer.planner', component: 'Contacts', params: { isPlanner: true, isFinal: false } }
    ];

    useEffect(() => {
        navigation.setOptions({ title: i18n.t(greet[Math.floor(new Date().getHours() / 6)]) });
        navigation.setOptions({ info: user.name });
    }, [user.name]);

    return (
        <>
            <Tab.Navigator
                screenOptions={{
                    tabBarLabelStyle: { fontFamily: 'Assistant_400Regular', fontWeight: 400, fontSize: 20, color: inAColors.secondary[50], textTransform: 'none' },
                    tabBarIndicatorStyle: { backgroundColor: inAColors.secondary[50] },
                    tabBarStyle: { backgroundColor: inAColors.primary[900] },
                    tabBarPressColor: inAColors.primary[700],
                    tabBarPressOpacity: inAColors.primary[700]
                }}>
                <Tab.Screen name="ActiveScreen" options={{ title: i18n.t('homescreen.navActive') }} component={ActiveScreen} />
                <Tab.Screen name="PlannedScreen" options={{ title: i18n.t('homescreen.navPlanned') }} component={PlannedScreen} />
            </Tab.Navigator>
            <Footer navigation={navigation} actions={actions} />
        </>
    );
}